import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import { Wallet, X, Loader2, Check } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

/**
 * Reusable payment modal — collects "paid from which account", method, amount,
 * date, and memo, then posts to the appropriate mark-paid endpoint. The
 * backend auto-posts the outflow to the finance ledger and deducts the
 * account balance. Idempotent per source.
 *
 * Props:
 *   - entity:        "purchase_order" | "material_request"
 *   - id:            string (PO id or MR id)
 *   - number:        display label (e.g. "PO-1234" or "REQ-5678")
 *   - defaultAmount: number to pre-fill in the amount field
 *   - onClose():     called when the user cancels/dismisses
 *   - onDone():      called on successful payment
 */
export function PayReceiptModal({ entity, id, number, defaultAmount = 0, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    paid_from_account_id: "",
    payment_method: "bank",
    amount: String(defaultAmount || 0),
    date: new Date().toISOString().slice(0, 10),
    memo: "",
  });

  useEffect(() => {
    let alive = true;
    api.get("/finance/accounts")
      .then((rows) => {
        if (!alive) return;
        const active = (rows || []).filter((a) => a.active !== false);
        setAccounts(active);
        if (active.length > 0) setForm((f) => ({ ...f, paid_from_account_id: active[0].id }));
      })
      .catch(() => setAccounts([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const endpointPath = entity === "purchase_order"
    ? `/purchase-orders/${id}/mark-paid`
    : `/material-requests/${id}/mark-paid`;

  const submit = async () => {
    if (!form.paid_from_account_id) { toast.error("Select an account"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      await api.post(endpointPath, {
        payment_method: form.payment_method,
        paid_from_account_id: form.paid_from_account_id,
        amount: amt,
        date: form.date,
        memo: form.memo,
      });
      toast.success("Payment recorded — posted to ledger");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to mark paid");
    } finally { setBusy(false); }
  };

  const title = entity === "purchase_order"
    ? `Pay PO ${number || id?.slice(0, 8)}`
    : `Pay receipt ${number || id?.slice(0, 8)}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={`pay-modal-${id}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><Wallet className="w-4 h-4" /> {title}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Records the outflow in the finance ledger &amp; deducts the selected account.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Paid from account *</label>
            {loading ? (
              <div className="text-xs text-slate-400 py-2"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading accounts...</div>
            ) : accounts.length === 0 ? (
              <div className="text-xs text-rose-600 py-2 border border-rose-200 rounded bg-rose-50 px-2">
                No bank accounts yet. Go to Finance → Accounts and create one first.
              </div>
            ) : (
              <select
                data-testid={`pay-account-${id}`}
                value={form.paid_from_account_id}
                onChange={(e) => setForm({ ...form, paid_from_account_id: e.target.value })}
                className={inputCls}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.type || "bank"} · {money(a.current_balance || 0)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Method</label>
              <select value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className={inputCls}>
                <option value="bank">Bank transfer</option>
                <option value="credit_card">Credit card</option>
                <option value="debit_card">Debit card</option>
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="wire">Wire</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Date</label>
              <input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Amount *</label>
            <input
              data-testid={`pay-amount-${id}`}
              type="text" inputMode="decimal"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^0-9.]/g, "") })}
              className={`${inputCls} text-right tabular-nums font-semibold`}
            />
            <div className="text-[10px] text-slate-400 mt-1">Suggested total: <span className="font-semibold">{money(defaultAmount)}</span></div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Memo</label>
            <input type="text" value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              placeholder={number || ""}
              className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-white">Cancel</button>
          <button
            data-testid={`pay-confirm-${id}`}
            onClick={submit}
            disabled={busy || accounts.length === 0 || !form.paid_from_account_id}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
          >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm payment</button>
        </div>
      </div>
    </div>
  );
}

export default PayReceiptModal;
